/**
 * Trovva — CSV Import for Deals/Transactions
 *
 * Simplified idiot-proof flow (mirrors CsvImport.jsx for contacts):
 *   1. Upload a .csv file (drag or click)
 *   2. AI silently analyzes & maps columns + normalizes data
 *   3. Clean preview: "Ready to import 23 deals — here's a sample"
 *   4. One big Import button → done
 *
 * Column mapping is hidden — only appears if user clicks "Adjust columns"
 * or if AI can't figure out the required fields.
 */
import { useState, useMemo } from 'react';
import { Upload, X, CheckCircle2, AlertCircle, Loader2, Sparkles, Settings2, HelpCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { normalizeRows } from '../../lib/csvNormalizer';

const TROVVA_FIELDS = [
  { key: '',                   label: '— ignore —' },
  { key: 'property_address',   label: 'Property Address' },
  { key: 'property_city',      label: 'City' },
  { key: 'property_state',     label: 'State' },
  { key: 'property_zip',       label: 'ZIP' },
  { key: 'full_address',       label: 'Full Address (auto-parsed)' },
  { key: 'deal_type',          label: 'Deal Type' },
  { key: 'status',             label: 'Status' },
  { key: 'offer_price',        label: 'Offer Price' },
  { key: 'sale_price',         label: 'Sale Price' },
  { key: 'commission_pct',     label: 'Commission %' },
  { key: 'offer_date',         label: 'Offer Date' },
  { key: 'acceptance_date',    label: 'Acceptance Date' },
  { key: 'closing_date',       label: 'Closing Date' },
  { key: 'client_first_name',  label: 'Client First Name' },
  { key: 'client_last_name',   label: 'Client Last Name' },
  { key: 'client_full_name',   label: 'Client Full Name (auto-split)' },
  { key: 'client_email',       label: 'Client Email' },
  { key: 'client_phone',       label: 'Client Phone' },
  { key: 'other_agent_name',   label: 'Other Agent' },
  { key: 'other_brokerage',    label: 'Other Brokerage' },
  { key: 'title_company',      label: 'Title Company' },
  { key: 'lender_name',        label: 'Lender' },
  { key: 'attorney_name',      label: 'Attorney' },
  { key: 'notes',              label: 'Notes' },
];

// Which columns to show in the clean preview table
const PREVIEW_COLS = ['property_address', 'property_city', 'property_state', 'offer_price', 'client_first_name', 'status'];
const PREVIEW_LABELS = {
  property_address: 'Address', property_city: 'City', property_state: 'State',
  offer_price: 'Offer Price', client_first_name: 'Client', status: 'Status',
};

// ── Minimal RFC-4180 CSV parser ──────────────────────────────
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\r') { /* skip */ }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else field += c;
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  while (rows.length && rows[rows.length - 1].every(v => !v.trim())) rows.pop();
  if (!rows.length) return { headers: [], rows: [] };
  return { headers: rows[0].map(h => h.trim()), rows: rows.slice(1) };
}

function guessField(header) {
  const h = (header || '').trim().toLowerCase().replace(/[_\-]+/g, ' ');
  const map = {
    address: 'property_address', 'property address': 'property_address', 'street address': 'property_address', street: 'property_address',
    city: 'property_city', state: 'property_state', zip: 'property_zip', zipcode: 'property_zip', 'zip code': 'property_zip',
    'deal type': 'deal_type', type: 'deal_type', 'transaction type': 'deal_type',
    status: 'status', stage: 'status',
    'offer price': 'offer_price', offer: 'offer_price', 'list price': 'offer_price',
    'sale price': 'sale_price', 'sold price': 'sale_price', price: 'sale_price',
    commission: 'commission_pct', 'commission %': 'commission_pct', 'comm %': 'commission_pct',
    'offer date': 'offer_date', 'acceptance date': 'acceptance_date', accepted: 'acceptance_date',
    'closing date': 'closing_date', 'close date': 'closing_date', closing: 'closing_date',
    'client first name': 'client_first_name', 'buyer first name': 'client_first_name', 'seller first name': 'client_first_name',
    'client last name': 'client_last_name', 'buyer last name': 'client_last_name', 'seller last name': 'client_last_name',
    'client name': 'client_full_name', 'buyer name': 'client_full_name', 'seller name': 'client_full_name',
    'client email': 'client_email', 'buyer email': 'client_email', 'seller email': 'client_email',
    'client phone': 'client_phone', 'buyer phone': 'client_phone', 'seller phone': 'client_phone',
    'other agent': 'other_agent_name', 'co op agent': 'other_agent_name', 'cooperating agent': 'other_agent_name',
    'other brokerage': 'other_brokerage', 'co op brokerage': 'other_brokerage',
    'title company': 'title_company', title: 'title_company',
    lender: 'lender_name', 'lender name': 'lender_name',
    attorney: 'attorney_name', 'attorney name': 'attorney_name',
    notes: 'notes', comments: 'notes',
  };
  return map[h] || null;
}

export default function CsvDealImport({ onClose, onDone }) {
  // Steps: 'upload' | 'analyzing' | 'needs_help' | 'preview' | 'importing' | 'done' | 'mapping'
  const [step, setStep] = useState('upload');
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [dedupe] = useState('address'); // Sensible default, no need to ask
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [aiMapping, setAiMapping] = useState(null);
  const [fileName, setFileName] = useState('');
  const [cameFromHelp, setCameFromHelp] = useState(false);

  const mappedRows = useMemo(() => {
    if (!rawRows.length) return [];
    if (aiMapping) return normalizeRows(rawRows, aiMapping, 'deals');
    return rawRows.map(r => {
      const out = {};
      Object.entries(mapping).forEach(([idx, field]) => {
        if (field) out[field] = (r[idx] || '').trim();
      });
      return out;
    });
  }, [rawRows, mapping, aiMapping]);

  const hasRequired = useMemo(() => {
    const used = Object.values(mapping);
    return used.includes('property_address') || used.includes('full_address');
  }, [mapping]);

  // Count how many fields were successfully mapped
  const mappedCount = useMemo(() => Object.values(mapping).filter(Boolean).length, [mapping]);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      const { headers, rows } = parseCsv(text);
      if (!headers.length) throw new Error('This file appears to be empty');
      if (rows.length === 0) throw new Error('This file has headers but no data');
      setRawHeaders(headers);
      setRawRows(rows);
      setStep('analyzing');

      // AI mapping
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch('/api/ai-csv-mapper', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ headers, sampleRows: rows.slice(0, 5), target: 'deals' }),
        });
        if (resp.ok) {
          const aiResult = await resp.json();
          setAiMapping(aiResult);
          const guess = {};
          Object.entries(aiResult.mappings || {}).forEach(([idx, info]) => {
            const field = typeof info === 'string' ? info : info?.field;
            if (field) guess[idx] = field;
          });
          setMapping(guess);
          // If we have required fields, go straight to preview
          const usedFields = Object.values(guess);
          const ready = usedFields.includes('property_address') || usedFields.includes('full_address');
          setStep(ready ? 'preview' : 'needs_help');
        } else {
          fallbackGuess(headers);
        }
      } catch (aiErr) {
        fallbackGuess(headers);
      }
    } catch (e) {
      setError(e.message);
      setStep('upload');
    }
  };

  const fallbackGuess = (headers) => {
    const guess = {};
    headers.forEach((h, i) => { const m = guessField(h); if (m) guess[i] = m; });
    setMapping(guess);
    const used = Object.values(guess);
    const ready = used.includes('property_address') || used.includes('full_address');
    setStep(ready ? 'preview' : 'needs_help');
  };

  const handleImport = async () => {
    setStep('importing');
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const resp = await fetch('/api/import-deals', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: mappedRows, dedupe }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Import failed');
      setResult(data);
      setStep('done');
      onDone?.(data);
    } catch (e) {
      setError(e.message);
      setStep('preview');
    }
  };

  const reset = () => {
    setStep('upload'); setRawHeaders([]); setRawRows([]); setMapping({});
    setResult(null); setError(null); setAiMapping(null); setFileName('');
    setCameFromHelp(false);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.colors.card, borderRadius: 16, width: '100%', maxWidth: 640,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${theme.colors.border}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Upload size={18} color={theme.colors.primary} />
          <h3 style={{ flex: 1, margin: 0, fontSize: 17, color: theme.colors.text }}>Import Deals</h3>
          <button onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* ── Upload ── */}
          {step === 'upload' && (
            <div>
              <label
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: 48, border: `2px dashed ${theme.colors.border}`, borderRadius: 12,
                  cursor: 'pointer', gap: 10, transition: 'border-color 0.2s',
                }}
              >
                <Upload size={36} color={theme.colors.textLight} />
                <div style={{ fontWeight: 600, fontSize: 15, color: theme.colors.text }}>
                  Drop your CSV here or click to browse
                </div>
                <div style={{ fontSize: 12, color: theme.colors.textLight }}>
                  Works with exports from any CRM, spreadsheet, or MLS
                </div>
                <input type="file" accept=".csv,text/csv" onChange={e => handleFile(e.target.files?.[0])} style={{ display: 'none' }} />
              </label>
              {error && <div style={errorBoxStyle}><AlertCircle size={14} /> {error}</div>}
            </div>
          )}

          {/* ── AI Analyzing ── */}
          {step === 'analyzing' && (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Sparkles size={36} color={theme.colors.primary} style={{ animation: 'pulse 1.5s ease-in-out infinite', marginBottom: 16 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text }}>
                Reading your file...
              </div>
              <div style={{ fontSize: 13, color: theme.colors.textLight, marginTop: 6 }}>
                Detecting addresses, prices, dates, and client info automatically
              </div>
              <style>{`@keyframes pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.1); } }`}</style>
            </div>
          )}

          {/* ── Preview (main screen) ── */}
          {step === 'preview' && (
            <div>
              {/* Summary bar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
                padding: 16, borderRadius: 10, background: '#F0FDF4', border: '1px solid #BBF7D0',
              }}>
                <CheckCircle2 size={24} color="#16A34A" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#166534' }}>
                    Ready to import {rawRows.length.toLocaleString()} deal{rawRows.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#15803D', marginTop: 2 }}>
                    {mappedCount} fields detected from {fileName}
                    {aiMapping && ' — mapped by AI'}
                  </div>
                </div>
              </div>

              {/* Clean preview table — just the key fields */}
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.textLight, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Sample preview
              </div>
              <div style={{ overflowX: 'auto', border: `1px solid ${theme.colors.border}`, borderRadius: 10, marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: theme.colors.bg }}>
                      {PREVIEW_COLS.filter(c => mappedRows[0]?.[c] !== undefined).map(c => (
                        <th key={c} style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: theme.colors.text, borderBottom: `1px solid ${theme.colors.border}` }}>
                          {PREVIEW_LABELS[c]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mappedRows.slice(0, 5).map((r, i) => (
                      <tr key={i}>
                        {PREVIEW_COLS.filter(c => mappedRows[0]?.[c] !== undefined).map(c => (
                          <td key={c} style={{
                            padding: '8px 12px', borderBottom: `1px solid ${theme.colors.border}40`,
                            color: theme.colors.text, maxWidth: 180, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {r[c] || <span style={{ color: theme.colors.textLight }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rawRows.length > 5 && (
                <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 16 }}>
                  ...and {(rawRows.length - 5).toLocaleString()} more
                </div>
              )}

              {error && <div style={errorBoxStyle}><AlertCircle size={14} /> {error}</div>}

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <button onClick={() => setStep('mapping')} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '8px 12px', borderRadius: 8, border: 'none',
                  background: 'transparent', color: theme.colors.textLight,
                  fontSize: 12, cursor: 'pointer',
                }}>
                  <Settings2 size={13} /> Adjust columns
                </button>
                <div style={{ flex: 1 }} />
                <button onClick={reset} style={btnGhost}>
                  Cancel
                </button>
                <button onClick={handleImport} style={{
                  ...btnPrimary, padding: '12px 28px', fontSize: 14,
                }}>
                  Import {rawRows.length.toLocaleString()} deals
                </button>
              </div>
            </div>
          )}

          {/* ── Importing ── */}
          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Loader2 size={36} color={theme.colors.primary} style={{ animation: 'spin 1s linear infinite', marginBottom: 16 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text }}>
                Importing {rawRows.length.toLocaleString()} deals...
              </div>
              <div style={{ fontSize: 13, color: theme.colors.textLight, marginTop: 6 }}>
                Normalizing addresses, prices, and dates
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* ── Done ── */}
          {step === 'done' && result && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <CheckCircle2 size={48} color="#16A34A" style={{ marginBottom: 12 }} />
              <h3 style={{ margin: '0 0 6px', fontSize: 20, color: theme.colors.text }}>
                {result.inserted} deal{result.inserted !== 1 ? 's' : ''} imported!
              </h3>
              {result.skipped_dupes > 0 && (
                <div style={{ color: theme.colors.textLight, fontSize: 13 }}>
                  {result.skipped_dupes} duplicate{result.skipped_dupes !== 1 ? 's' : ''} skipped (address already in Trovva)
                </div>
              )}
              {result.validation_errors?.length > 0 && (
                <details style={{ marginTop: 16, textAlign: 'left' }}>
                  <summary style={{ cursor: 'pointer', color: theme.colors.warning, fontWeight: 600, fontSize: 13 }}>
                    {result.validation_errors.length} row{result.validation_errors.length !== 1 ? 's' : ''} had issues
                  </summary>
                  <div style={{ marginTop: 6, fontSize: 12, color: theme.colors.textLight, maxHeight: 150, overflowY: 'auto' }}>
                    {result.validation_errors.slice(0, 20).map((er, i) => (
                      <div key={i} style={{ padding: '3px 0' }}>Row {er.row}: {er.reason}</div>
                    ))}
                  </div>
                </details>
              )}
              <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={reset} style={btnGhost}>Import another</button>
                <button onClick={() => { onDone?.(); onClose(); }} style={btnPrimary}>Done</button>
              </div>
            </div>
          )}

          {/* ── Needs Help — AI couldn't find required fields ── */}
          {step === 'needs_help' && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: 20, borderRadius: 10,
                background: '#FFFBEB', border: '1px solid #FDE68A', marginBottom: 20,
              }}>
                <AlertCircle size={24} color="#D97706" style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>
                    We need a little help with your file
                  </div>
                  <div style={{ fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
                    Trovva couldn't automatically detect which column contains the <strong>property address</strong>.
                    Every deal needs at least an address to be imported. Your file has {rawHeaders.length} columns
                    and {rawRows.length.toLocaleString()} rows — we just need you to tell us which column is which.
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 13, color: theme.colors.textLight, marginBottom: 6 }}>
                Here are your CSV column headers:
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6, padding: 12, borderRadius: 8,
                background: theme.colors.bg, border: `1px solid ${theme.colors.border}`, marginBottom: 20,
              }}>
                {rawHeaders.map((h, i) => (
                  <span key={i} style={{
                    padding: '4px 10px', borderRadius: 6, background: theme.colors.card,
                    border: `1px solid ${theme.colors.border}`, fontSize: 12, color: theme.colors.text,
                  }}>{h || `Column ${i + 1}`}</span>
                ))}
              </div>

              <button onClick={() => { setCameFromHelp(true); setStep('mapping'); }} style={{
                ...btnPrimary, width: '100%', justifyContent: 'center', padding: '14px 20px', fontSize: 14,
              }}>
                <Settings2 size={16} /> Fix column mapping
              </button>

              <button onClick={reset} style={{
                ...btnGhost, width: '100%', justifyContent: 'center', marginTop: 10,
              }}>
                Try a different file
              </button>
            </div>
          )}

          {/* ── Manual mapping (hidden by default) ── */}
          {step === 'mapping' && (
            <div>
              {/* Detailed instructions */}
              <div style={{
                padding: 14, borderRadius: 10, marginBottom: 16,
                background: '#EFF6FF', border: '1px solid #BFDBFE',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <HelpCircle size={14} color="#2563EB" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1E40AF' }}>How this works</span>
                </div>
                <div style={{ fontSize: 12, color: '#1E40AF', lineHeight: 1.6 }}>
                  Each row below shows one of your CSV columns on the left. Use the dropdown on the right
                  to tell Trovva what that column contains. {cameFromHelp ? (
                    <>You <strong>must</strong> assign at least a <strong>Property Address</strong> column
                    (or a <strong>Full Address</strong> column if your file has the full address in one field).</>
                  ) : (
                    <>Set any column to "— ignore —" if you don't need it.</>
                  )} Trovva will automatically format prices, dates, phone numbers, and addresses during import.
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
                {rawHeaders.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 8,
                      background: theme.colors.bg, fontSize: 12, color: theme.colors.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{h || `Column ${i + 1}`}</div>
                    <span style={{ color: theme.colors.textLight, fontSize: 11 }}>→</span>
                    <select
                      value={mapping[i] || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setMapping(m => ({ ...m, [i]: val }));
                        if (aiMapping) {
                          setAiMapping(prev => ({
                            ...prev,
                            mappings: { ...prev.mappings, [i]: { field: val, confidence: 1 } },
                          }));
                        }
                      }}
                      style={{
                        flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 8,
                        border: `1px solid ${theme.colors.border}`, fontSize: 12,
                      }}
                    >
                      {TROVVA_FIELDS.map(f => (
                        <option key={f.key} value={f.key}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {!hasRequired && (
                <div style={{
                  marginTop: 12, padding: 12, background: '#FEF2F2', color: '#991B1B',
                  borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8,
                  border: '1px solid #FECACA',
                }}>
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong>Required:</strong> Assign at least a <strong>Property Address</strong> column
                    (or <strong>Full Address</strong> if address, city, state, and zip are in one field).
                    Look through your CSV columns above and use the dropdowns to match them.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button onClick={() => { setCameFromHelp(false); setStep('preview'); }} disabled={!hasRequired} style={btnGhost}>
                  Back to preview
                </button>
                <button onClick={() => { setCameFromHelp(false); setStep('preview'); }} disabled={!hasRequired} style={{
                  ...btnPrimary, opacity: hasRequired ? 1 : 0.5,
                }}>
                  Save & continue
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Styles as constants (not re-created each render)
const iconBtn = {
  background: 'transparent', border: `1px solid ${theme.colors.border}`,
  color: theme.colors.text, borderRadius: 8, width: 30, height: 30,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 16px', borderRadius: 10, border: 'none',
  background: theme.colors.primary, color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
};
const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 14px', borderRadius: 10, background: 'transparent',
  border: `1px solid ${theme.colors.border}`, color: theme.colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const errorBoxStyle = {
  marginTop: 12, padding: 10, background: '#FEF2F2', color: theme.colors.danger,
  borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
};
